/**
 * Publish a draft: when it binds, and whether clients must accept it again.
 *
 * Publishing is the one irreversible step — once a version is in effect its
 * text can never change, and every acceptance of it points at exactly those
 * words — so the dialog says what will happen in plain terms before it happens.
 * A scheduled version can still be taken back until it takes effect.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  Alert,
  Button,
  Input,
  Modal,
  Switch,
  formatDateTime,
  toast,
  api,
} from '@connect/web-shared';

import { legalKeys, localInputToInstant, type VersionDetail } from './types';

export function PublishVersionDialog({
  open,
  onClose,
  version,
  isFirst,
  currentLabel,
}: {
  open: boolean;
  onClose: () => void;
  version: VersionDetail;
  /** Nothing published before: there is nothing to keep an acceptance of. */
  isFirst: boolean;
  currentLabel: string | null;
}) {
  const queryClient = useQueryClient();
  const [when, setWhen] = useState<'now' | 'later'>('now');
  const [at, setAt] = useState('');
  const [reconsent, setReconsent] = useState(true);

  useEffect(() => {
    if (!open) return;
    setWhen('now');
    setAt('');
    setReconsent(true);
  }, [open]);

  const effectiveAt = when === 'later' ? localInputToInstant(at) : null;

  const publish = useMutation({
    mutationFn: () =>
      api.post<VersionDetail>(`/legal/versions/${version.public_id}/publish`, {
        effective_at: effectiveAt,
        requires_reconsent: isFirst || reconsent,
      }),
    onSuccess: async (published) => {
      await queryClient.invalidateQueries({ queryKey: legalKeys.all });
      toast.success(
        `${published.label} published`,
        when === 'later'
          ? `Takes effect ${formatDateTime(published.effective_at)}.`
          : 'Clients see it now.',
      );
      onClose();
    },
  });

  const valid = when === 'now' || effectiveAt !== null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdrop={false}
      title={`Publish ${version.label}`}
      description="A published version can never be edited. Later changes are a new version."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => publish.mutate()} loading={publish.isPending} disabled={!valid}>
            Publish
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {publish.error instanceof ApiError ? (
          <Alert tone="danger">{publish.error.message}</Alert>
        ) : null}

        <fieldset className="space-y-2">
          <legend className="text-xs font-medium text-muted">Takes effect</legend>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="when"
              checked={when === 'now'}
              onChange={() => setWhen('now')}
            />
            Now
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="when"
              checked={when === 'later'}
              onChange={() => setWhen('later')}
            />
            On a date
          </label>
          {when === 'later' ? (
            <Input
              type="datetime-local"
              aria-label="Effective from"
              value={at}
              onChange={(event) => setAt(event.target.value)}
              hint="Your local time. Until then clients keep seeing the version in effect."
            />
          ) : null}
        </fieldset>

        {isFirst ? (
          <Alert tone="info">
            The first version: every client a requirement applies to is asked to accept it.
          </Alert>
        ) : (
          <Switch
            label="Clients must accept it again"
            hint={
              reconsent
                ? `Clients who accepted ${currentLabel ?? 'an earlier version'} are asked again. A mandatory requirement blocks the app until they accept.`
                : 'Earlier acceptances still count. Use this only for a correction that changes nothing clients agreed to.'
            }
            checked={reconsent}
            onChange={(event) => setReconsent(event.target.checked)}
          />
        )}
      </div>
    </Modal>
  );
}
